import React, { useEffect, useState } from 'react'
import { Box, Typography, Fade, Zoom, Grow } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import { keyframes } from '@mui/system'

// Define animations
const bounceIn = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`

const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  50% {
    box-shadow: 0 0 0 30px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
`

const pulseGlowRed = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }
  50% {
    box-shadow: 0 0 0 30px rgba(244, 67, 54, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
`

const slideInUp = keyframes`
  0% {
    transform: translateY(50px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`

const AnimatedResultScreen = ({ type, message }) => {
  const [showContent, setShowContent] = useState(false)
  const isSuccess = type === 'success'

  useEffect(() => {
    // Trigger content animation after a short delay
    const timer = setTimeout(() => {
      setShowContent(true)
    }, 100)
    return () => clearTimeout(timer)
  }, [])


  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        width: '100%',
        overflow: 'hidden',
        py: 4,
      }}
    >
      {/* Main content container */}
      <Fade in={showContent} timeout={800}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 2,
          }}
        >
          {/* Animated icon */}
          <Box
            sx={{
              animation: `${bounceIn} 0.8s ease-out`,
              mb: 3,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: 120, sm: 150 },
                height: { xs: 120, sm: 150 },
                borderRadius: '50%',
                backgroundColor: isSuccess
                  ? 'rgba(76, 175, 80, 0.1)'
                  : 'rgba(244, 67, 54, 0.1)',
                animation: isSuccess
                  ? `${pulseGlow} 2s infinite`
                  : `${pulseGlowRed} 2s infinite`,
              }}
            >
              <CheckCircleOutlineIcon
                sx={{
                  fontSize: { xs: 80, sm: 100 },
                  color: '#4CAF50',
                }}
              />
            </Box>
          </Box>

          {/* Title */}
          <Grow in={showContent} timeout={1000} style={{ transformOrigin: 'center center' }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#4CAF50',
                mb: 2,
                textAlign: 'center',
                animation: `${slideInUp} 0.6s ease-out 0.3s both`,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              }}
            >
              Success!
            </Typography>
          </Grow>

          {/* Message */}
          <Zoom in={showContent} timeout={1200}>
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                textAlign: 'center',
                maxWidth: '500px',
                px: 2,
                animation: `${slideInUp} 0.6s ease-out 0.5s both`,
                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                lineHeight: 1.6,
              }}
            >
              {message || (isSuccess
                ? 'Your purchase order has been approved successfully!'
                : 'Your purchase order has been rejected.'
              )}
            </Typography>
          </Zoom>
        </Box>
      </Fade>
    </Box>
  )
}

export default AnimatedResultScreen
